import { campaignTypes, campaignWidgetStyle, serverCampaignTypes } from 'config/constants';
import {
  generalDiscountMode,
  generalDiscounType,
  productSelection,
  quantityDiscountType,
  statusEnum,
  volumeMinApplyType,
  volumeMinType,
} from './enum';
import { queueFromStoredTakeovers } from 'utils/pendingTakeovers';

function campaignType(campaign) {
  if (campaign.type === serverCampaignTypes.BOGO) {
    return 6; // BOGO type
  }
  if (campaign.type === serverCampaignTypes.Shipping) {
    return 7; // Shipping type
  }
  if (campaign.type === serverCampaignTypes.Bundle) {
    return 8; // Bundle type
  }
  if (campaign.type === serverCampaignTypes.CrossSell) {
    return 9; // Bundle type
  }
  if (campaign.type === serverCampaignTypes.Upsell) {
    return 10; // Upsell type
  }
  if (campaign.type === serverCampaignTypes.FreeGift) {
    return 11; // Free Gift type
  }
  if (campaign.type === 'general') {
    return 0;
  } else if (campaign.type === 'order') {
    return 5;
  } else if (
    campaign?.volumeDiscountMinimumType === 'quantity' &&
    campaign.volumeDiscountMinimumApplyType === 'all_items'
  ) {
    return 1;
  } else if (
    campaign?.volumeDiscountMinimumType === 'quantity' &&
    campaign.volumeDiscountMinimumApplyType === 'each_item'
  ) {
    return 2;
  } else if (
    campaign?.volumeDiscountMinimumType === 'amount' &&
    campaign.volumeDiscountMinimumApplyType === 'all_items'
  ) {
    return 3;
  } else if (
    campaign?.volumeDiscountMinimumType === 'amount' &&
    campaign.volumeDiscountMinimumApplyType === 'each_item'
  ) {
    return 4;
  }
}

function getStatus(campaign) {
  if (
    campaign.status === 'in_progress' &&
    campaign.inProgressStatus === 'preventing_shopify_discount'
  ) {
    return 11;
  } else if (
    campaign.status === 'in_progress' &&
    campaign.inProgressStatus === 'reverting_shopify_discount'
  ) {
    return 12;
  }

  return statusEnum[campaign.status];
}

const onConvertDiscountType = (type) => {
  switch (type) {
    case serverCampaignTypes.General:
      return campaignTypes.General;

    case serverCampaignTypes.Order:
      return campaignTypes.Order;

    case serverCampaignTypes.Volume:
      return campaignTypes.Volume;

    case serverCampaignTypes.BOGO:
      return campaignTypes.BOGO;

    case serverCampaignTypes.Shipping:
      return campaignTypes.Shipping;

    case serverCampaignTypes.Bundle:
      return campaignTypes.Bundle;

    case serverCampaignTypes.CrossSell:
      return campaignTypes.CrossSell;

    case serverCampaignTypes.Wholesale:
      return campaignTypes.Wholesale;

    case serverCampaignTypes.Dropshipper:
      return campaignTypes.Dropshipper;

    case serverCampaignTypes.Upsell:
      return campaignTypes.Upsell;

    case serverCampaignTypes.FreeGift:
      return campaignTypes.FreeGift;

    default:
      return campaignTypes.General;
  }
};

export function baseSingleCampaignFormAdaptor(
  /** @type {import('rest/dist/gen/models/campaignDto').CampaignDto} */ campaign
) {
  const data = {
    id: campaign.id,
    publishStatus: 1,
    name: campaign.name,
    startDate: campaign.status === 'active' ? campaign.ranAt : campaign.startAt,
    createdDate: campaign.createdAt,
    updatedDate: campaign.updatedAt,
    endDate: campaign.endAt,
    newPrice: campaign.generalDiscountValue,
    discountPercentage: campaign.generalDiscountValue,
    allConflicts: campaign.allConflicts || [],
    noDiscount: true,
    roundDigit:
      campaign.generalDiscountCustomCent !== null && campaign.generalDiscountCustomCent >= 0
        ? campaign.generalDiscountCustomCent * 100
        : null,
    isDeleted: false,
    // A real Draft saves as a draft, same as Deactive — only running/
    // scheduled campaigns re-activate on save.
    isActive: campaign.status !== 'in_active' && campaign.status !== 'draft',
    isLive: campaign.status === 'active',
    isImmediately: campaign.isImmediately,
    isPendingOperate: campaign.status !== 'need_upgrade',
    hasTimeTinStore: true,
    discountIconInProduct: 'string',
    discountColor: 'string',
    colorAfterDiscount: 'string',
    quantityDiscountRules:
      campaign?.type === serverCampaignTypes.BOGO ||
      campaign?.type === serverCampaignTypes.Shipping ||
      campaign?.type === serverCampaignTypes.Bundle ||
      campaign?.type === serverCampaignTypes.CrossSell ||
      campaign?.type === serverCampaignTypes.Upsell ||
      campaign?.type === serverCampaignTypes.FreeGift
        ? campaign?.tiers || []
        : campaign?.tiers?.map((x) => ({
            discountValue: x.discountValue,
            minValue: x.minimumValue,
            maxValue: x.maximumValue,
            discountType: quantityDiscountType[campaign?.volumeDiscountType],
            minType: volumeMinType[campaign?.volumeDiscountMinimumType],
          })) ?? [],
    totalSale: campaign.totalRevenue,
    auctionTypeProductSelection: productSelection[campaign.productInclusionType],
    auctionTypeProductExclude: productSelection[campaign.productExclusionType],
    auctionType: generalDiscounType[campaign.generalDiscountType],
    auctionDiscountType: generalDiscountMode[campaign.generalDiscountPriceChangeMode],
    status: getStatus(campaign),
    inProgressStatus: campaign.inProgressStatus,
    estimatedProductsCount: campaign.estimatedProductsCount,
    estimatedVariantsCount: campaign.estimatedVariantsCount,
    // GROSS scope as the merchant configured it (enforcing + held by another
    // live campaign). estimatedProductsCount is the NET number; the grid's
    // "Products" column shows this one so a conflict never reads as the scope
    // shrinking. Older backend payloads without the field fall back to net.
    scopeProductsCount: campaign.scopeProductsCount ?? campaign.estimatedProductsCount,
    scopeVariantsCount: campaign.scopeVariantsCount ?? campaign.estimatedVariantsCount,
    updatedVariantCount: campaign?.progress?.done || 0,
    inProgressPercentage: 0,
    //TODO
    queue_progress_type: ['activating', 'preventing_shopify_discount'].includes(
      campaign.inProgressStatus
    )
      ? 0
      : 1,
    needUpgradeDueToDowngrading: campaign.needUpgradeDueToDowngrading,
    lockEditing: campaign.isLocked,
    // Why this campaign is blocked (variant_limit / campaign_type / plan_access),
    // computed by the backend so the lock banner can show the exact reason.
    blockReason: campaign.blockReason ?? null,
    // blocked by the plan while it was already Deactive: the status badge says it cannot be activated
    blockedWhileInactive: Boolean(campaign.blockedWhileInactive),
    // Draft saved because it would own no trigger / offer after conflict resolution (null otherwise)
    cannotActivate: campaign.cannotActivate ?? null,
    //TODO
    includeOnFly: campaign.autoUpdateIncluded,
    excludeOnFly: campaign.autoUpdateExcluded,
    handleCompareAtPrice: campaign.generalDiscountHandleCompareAtPrice,
    // canForceStop: campaign.cancelable && campaign.status === 'in_progress',
    canForceStop: false,
    isArchive: false,
    hasBulkDiscount: onConvertDiscountType(campaign.type),
    hasQuantityDiscount:
      campaign.type === 'volume' || campaign.type === serverCampaignTypes.Wholesale,
    isActionRequired: false,
    //TODO
    isApplyOncePerOrder: campaign.volumeDiscountIsOncePerOrder,
    quantityDiscountMinType: volumeMinType[campaign?.volumeDiscountMinimumType],
    quantityDiscountMinApplyType: volumeMinApplyType[campaign.volumeDiscountMinimumApplyType],
    orderCounts: campaign.orderCount,
    type: campaignType(campaign),
    processingProgress: campaign.processingProgress,
    isOrderDiscountCombined: campaign.isOrderDiscountCombined,
    // Load orderExecutionOrder too — the discount function reads THIS field
    // (BEFORE/AFTER), not isOrderDiscountCombined. Without it the form holds
    // `undefined`, so on save it's sent as undefined and Prisma skips the
    // column → the BEFORE/AFTER value silently never updates.
    orderExecutionOrder: campaign.orderExecutionOrder,
    hasProductCountdown: campaign.hasProductCountdown,
    activeByDiscountCode: campaign.activeByDiscountCode,
    discountCode: campaign.discountCode,
    discountCodeId: campaign.discountCodeId,
    conflictedProductsCount: campaign.conflictedProductsCount,
    conflictedVariantsCount: campaign.conflictedVariantsCount,
    discountCodeAppliesOncePerCustomer: campaign.discountCodeAppliesOncePerCustomer,
    // Discount-code method (single shared code vs bulk unique codes)
    codeMethod: campaign.codeMethod,
    codeType: campaign.codeType,
    usageLimitPerCode: campaign.usageLimitPerCode,
    perCustomerLimit: campaign.perCustomerLimit,
    codeRandomLength: campaign.codeRandomLength,
    codeCount: campaign.codeCount,
    codePrefix: campaign.codePrefix,
    codeGenerationStatus: campaign.codeGenerationStatus,
    // True once any of this campaign's codes was redeemed (an order used a code) —
    // the form locks the discount-code section on it.
    hasCodeRedemptions: campaign.hasCodeRedemptions,
    auctionTypeCustomerSelection: campaign.auctionTypeCustomerSelection,
    customerSegments: campaign.customerSegments
      ? campaign.customerSegments.map((x) => ({
          name: x.Name,
          id: x.shopifyId,
          isActive: true,
          isExclude: x.isExcluded,
          isDeleted: false,
        }))
      : [],
    customerTags: campaign.customerTags
      ? campaign.customerTags.map((x) => ({
          name: x.tagName,
          id: x.tagName,
          isActive: true,
          isExclude: x.isExcluded,
          isDeleted: false,
        }))
      : [],
    customers: campaign.customers
      ? campaign.customers.map((x) => ({
          ...x,
          id: x.shopifyId,
          name: x.firstName,
          isActive: true,
          isDeleted: false,
          isExclude: x.isExcluded,
        }))
      : [],
    widgetStyle: campaign.widgetStyle || campaignWidgetStyle.none,
    abDiscountRules: campaign?.A_b_tiers || [],
    isShowOnProductPage: campaign.isShowOnProductPage || false,
    isShowOnCartPage: campaign.isShowOnCartPage || false,
    isShowWidgetOnThisCampaign: campaign.isShowWidgetOnThisCampaign || false,
    showShippingProgressBar: campaign.isShowWidgetOnThisCampaign || false,
    hasSalesBadge: campaign?.hasSalesBadge || false,
    // Show the saved text as-is (empty stays empty so the placeholder hint shows);
    // don't substitute a literal default that the merchant never chose.
    salesBadgeText: campaign?.salesBadgeText ?? '',
    salesBadgeTranslations: campaign?.salesBadgeTranslations || undefined,
    salesBadgeTextColor: campaign?.salesBadgeTextColor || '#FFFFFF',
    salesBadgeBackgroundColor: campaign?.salesBadgeBackgroundColor || '#FF0000',
    salesBadgeType: campaign?.salesBadgeType || 'circle',
    salesBadgePosition: campaign?.salesBadgePosition || 'top_left',
    salesBadgeHasBorder: campaign?.salesBadgeHasBorder || false,
    conflictManagementTempModel:campaign?.conflictManagementTempModel,
    pendingTakeovers: campaign?.pendingTakeovers ?? [],
    // Baseline for the form's dirty check: the queue the editor rebuilds
    // from pendingTakeovers on load must not count as an unsaved change.
    pendingGroupResolutions: campaign?.pendingTakeovers?.length
      ? JSON.stringify(queueFromStoredTakeovers(campaign.pendingTakeovers))
      : undefined,
    products: campaign?.products,
    generalDiscountValue: campaign.generalDiscountValue ?? null,
    newPriceDiscountType: (() => { const i = ['percentage', 'fix_price', 'new_price'].indexOf(campaign.newPriceDiscountType); return i === -1 ? 0 : i; })(),
    newPriceChangeType: (() => { const i = ['increase', 'decrease'].indexOf(campaign.newPriceChangeType); return i === -1 ? 1 : i; })(),
    generalDiscountHasAdjustCents: campaign.generalDiscountCustomCent != null,
    generalDiscountCustomCent: campaign.generalDiscountCustomCent ?? null,
    hasPriceProtection: campaign.hasPriceProtection ?? false,
    generalDiscountPriceFloor: campaign.PriceFloor ?? null,
    // ── Wholesale / B2B Pricing (load saved config on edit) ──
    hasProfitProtection: campaign?.hasProfitProtection ?? false,
    minMarginForProfit: campaign?.minMarginForProfit ?? null,
    wholesaleOnMargin: campaign?.wholesaleOnMargin ?? false,
    wholesaleMinProfitType: campaign?.wholesaleMinProfitType ?? 'fixed',
    wholesaleMissingCostRule: campaign?.wholesaleMissingCostRule ?? 'skip',
    wholesaleOnlyEligible: campaign?.wholesaleOnlyEligible ?? true,
    wholesaleProgressBar: campaign?.wholesaleProgressBar ?? null,
    // ── Dropshipper Pricing (load saved "Based on" dimension on edit) ──
    dropshipperPricingBase: campaign?.dropshipperPricingBase ?? 1,
    shippingTitle: campaign?.shippingTitle ?? '',
    // The server column is nullable: a saved rule list with a null flag is still an active rule
    // (never hydrate it as "off", or the next save would drop the merchant's rule).
    hasPurchaseHistoryCondition:
      campaign?.hasPurchaseHistoryCondition ??
      (Array.isArray(campaign?.purchaseHistory) && campaign.purchaseHistory.length > 0),
    purchaseHistory: campaign?.purchaseHistory ?? [],
    isAllMarkets: campaign?.isAllMarkets ?? true,
    selectedMarkets: campaign?.selectedMarkets ?? [],
    excludedCountries: campaign?.excludedCountries ?? [],
    shippingNoLargerThanProductPrice: campaign?.notAllowMoreThanProductPrice ?? false,
    shippingConvertToFreeShipping: campaign?.convertLargerToFreeShip ?? false,
    shippingOfferType: campaign?.shippingOfferType ?? null,
    shippingCombination: {
      combineWithOtherDiscounts: campaign?.shippingCombineWithOtherDiscounts ?? false,
      allowProductDiscounts:     campaign?.shippingAllowProductDiscounts ?? true,
      allowOrderDiscounts:       campaign?.shippingAllowOrderDiscounts ?? false,
      executionOrder:            campaign?.shippingExecutionOrder ?? 'before',
    },
    freeShippingConditions: {
      freeShippingLimitEnabled: campaign?.limitFreeShipping ?? false,
      freeShippingMaxRate: campaign?.maxShippingRateCovered ?? '',
      freeShippingExceedAction: campaign?.exceedLimitAction ?? 'do_not_apply',
      freeShippingExcludeRemoteZones: campaign?.excludeRemoteZones ?? false,
    },
    bogoConfig: campaign?.bogoConfig ?? null,
    // BXGY reward fields — the campaigns LIST endpoint returns these flat (it does NOT build a
    // reshaped `bogoConfig`, which only exists on the detail endpoint). The grid's Discount
    // column uses them to label Buy X Get Y campaigns instead of falling back to legacy tiers.
    bxgyRewardType: campaign?.bxgyRewardType ?? null,
    bxgyRewardPercentage: campaign?.bxgyRewardPercentage ?? null,
    bxgyRewardFixedAmount: campaign?.bxgyRewardFixedAmount ?? null,
    // Product Bundle set config (detail endpoint; reshaped in get.adaptor.js) and
    // the profit-protection action that only set types send.
    bundleConfig: campaign?.bundleConfig ?? null,
    bellowMarginAction: campaign?.bellowMarginAction ?? 'reduceRewards',
    // Flat reward fields for the campaigns LIST (grid Discount column), when the
    // list endpoint provides them instead of the full bundleConfig.
    // Combo status chip (spec D3/D4): flat fields on the list payload, or the
    // detail bundleConfig.combo when that is what the endpoint returns.
    // Backend flat summary (comboStatusSummary): comboStatus is null when combo is off.
    bundleComboEnabled: campaign?.comboStatus != null || !!campaign?.bundleConfig?.combo?.enabled,
    bundleComboStatus: campaign?.comboStatus ?? null,
    bundleComboReasonCount: campaign?.comboReasonCount ?? null,
    bundleComboProductId: campaign?.comboProductId ?? campaign?.bundleConfig?.combo?.productId ?? null,
    bundleComboDrift: campaign?.comboNotice?.kind === 'drift_draft' && !campaign?.comboNotice?.read,
    bundleRewardType: campaign?.bundleRewardType ?? campaign?.bundleConfig?.reward?.type ?? null,
    bundleRewardValue: campaign?.bundleRewardValue ?? campaign?.bundleConfig?.reward?.value ?? null,
    // set ladder (3 sets 10%, 6 sets 20%): the grid shows the whole range, not just the first rung
    bundleSetTiers: Array.isArray(campaign?.bundleSetTiers)
      ? campaign.bundleSetTiers
      : campaign?.bundleConfig?.setTiers ?? [],
    // Cross-sell trigger/offer/reward config (detail endpoint; reshaped in
    // get.adaptor.js) + the same flat reward fields for the campaigns LIST.
    xsellConfig: campaign?.xsellConfig ?? null,
    xsellRewardType: campaign?.xsellRewardType ?? campaign?.xsellConfig?.reward?.type ?? null,
    xsellRewardValue: campaign?.xsellRewardValue ?? campaign?.xsellConfig?.reward?.value ?? null,
    // Attach rate (offer add-through), when the list/analytics endpoint sends
    // it (B2.6). Left undefined rather than 0 until the backend confirms the
    // field name — see the final report's "needs reconciling" note.
    xsellAttachRate: campaign?.xsellAttachRate ?? null,
    // Free Gift scope/tiers/display config (detail endpoint; reshaped in
    // get.adaptor.js) + flat tier fields for the campaigns LIST (grid
    // Discount column, see CampaignsTable.js's getDiscount()), same
    // fallback-to-full-config convention as Bundle's/Cross-sell's flat
    // reward fields above. Field names (`giftTierCount`/`giftFirstTierMinSpend`)
    // are a guess pending the backend agent's real list DTO — flagged in the
    // final report for reconciliation.
    giftConfig: campaign?.giftConfig ?? null,
    giftTierCount: campaign?.giftTierCount ?? campaign?.giftConfig?.tiers?.length ?? null,
    giftFirstTierMinSpend:
      campaign?.giftFirstTierMinSpend ?? campaign?.giftConfig?.tiers?.[0]?.minSpend ?? null,
    // Upsell trigger/ladder/offer/incentive config (detail endpoint; reshaped
    // in get.adaptor.js) + flat fields for the campaigns LIST (grid Discount
    // column, see CampaignsTable.js's getDiscount()) and the swap count the
    // order webhook attributes (upsell plan §2.2/§2.8). Same
    // fallback-to-full-config convention as Bundle's/Cross-sell's flat fields.
    upsellConfig: campaign?.upsellConfig ?? null,
    upsellMode: campaign?.upsellMode ?? campaign?.upsellConfig?.mode ?? null,
    upsellIncentiveType: campaign?.upsellIncentiveType ?? campaign?.upsellConfig?.incentive?.type ?? null,
    upsellIncentiveValue: campaign?.upsellIncentiveValue ?? campaign?.upsellConfig?.incentive?.value ?? null,
    // null (not 0) until the backend sends it — never a confident zero.
    upsellSwaps: campaign?.upsellSwaps ?? null,
    templateId: campaign?.templateId ?? null,
  };
  return data;
}

export function getCampaignsAdaptors(campaigns) {
  const data = {
    data: campaigns.map((cmp) => baseSingleCampaignFormAdaptor(cmp)),
    success: true,
    message: 'ok',
  };

  return data;
}
